import { Button, Col, Divider, Row, Select } from "antd";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import AppContext from "../../../context/app/context";
import AreaContext from "../../../context/discount-areas/context";
import AreaContextProvider from "../../../context/discount-areas/provider";
import { getAreaStatus } from "../../../models/discount-areas/enum";
import { IUpdateArea } from "../../../models/discount-areas/request";
import { ICity } from "../../../models/city/response";
import EndPoints from "../../../services/end-points";
import { languages } from "../../../utils/helpers/constants";
import { execute } from "../../../utils/helpers/execute";
import { validatePolygonCoordinates } from "../../../utils/helpers/polygon-validation";
import {
  getDumpTranslations,
  getTranslations,
} from "../../../utils/helpers/translations";
import Controller from "../../form-components/controller";
import FieldBuilder from "../../form-components/field-builder";
import Spin from "../../general/antd/spin";
import FormItem from "../../general/form-item";
import styles from "./style.module.scss";
import CombinedMap from "../../general/map/new-component";
import { IStore } from "../../../models/store/response";
import { IResponseTracking } from "../../../models/map/response";
import { ITag } from "../../../models/tag/response";

const Form = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { details, actions, loading } = useContext(AreaContext);
  const { direction } = useContext(AppContext);
  const [cities, setCities] = useState<ICity[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(true);
  const [storesLoading, setStoresLoading] = useState(true);
  const status = [1, 2];
  const { id } = useParams();
  const [area, setArea] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [loadingArea, setLoadingArea] = useState(true);
  const [mapGeometryIssues, setMapGeometryIssues] = useState({
    outsideServiceArea: 0,
    currentNeighborOverlapArea: 0,
    neighborNeighborOverlapArea: 0,
    polygonIssueCount: 0,
  });
  const [storesData, setStoresData] = useState<IStore[]>([]);
  const [driversLocation, setDriversLocation] = useState<IResponseTracking[]>([]);
  const [pharmacyPromotionFilter, setPharmacyPromotionFilter] = useState<boolean | null>(null);
  const [operationStoreCategories, setOperationStoreCategories] = useState<ITag[]>([]);
  const [operationStoreCategoriesLoading, setOperationStoreCategoriesLoading] = useState(true);
  const [operationStoreCategoryFilter, setOperationStoreCategoryFilter] = useState<number | null>(null);

  const getAreas = async (cityId) => {
    if (!cityId) return;
    setLoadingArea(true);
    setArea([]);
    setServiceAreas([]);
    setMapGeometryIssues({
      outsideServiceArea: 0,
      currentNeighborOverlapArea: 0,
      neighborNeighborOverlapArea: 0,
      polygonIssueCount: 0,
    });
    try {
      const filters = [
        {
          name: "cityId",
          value: cityId,
          operation: "eq",
        },
        {
          name: "status",
          value: 1,
          operation: "eq",
        }
      ];
      const [discountAreasResponse, serviceAreasResponse] = await Promise.all([
        EndPoints.discountarea.getAllAreas({
          perPage: -1,
          filters,
        }),
        EndPoints.area.getAllAreas({
          perPage: -1,
          filters,
        }),
      ]);
      const allPoints = discountAreasResponse.data.data.map((el) => ({
        id: el.id,
        points: el.points,
      }));
      const serviceAreaPoints = serviceAreasResponse.data.data.map((el) => ({
        id: el.id,
        points: el.points,
      }));
      setArea(allPoints);
      setServiceAreas(serviceAreaPoints);
      setLoadingArea(false);
    } catch (error) {
    } finally {
      setLoadingArea(false);
    }
  };

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  useEffect(() => {
    details && getAreas(details?.city?.id);
  }, [details]);

  // get all cities from api
  useEffect(() => {
    const getcities = async () => {
      try {
        const { data } = await EndPoints.city.getAllCities({
          light: true,
          perPage: -1,
        });
        setCities(data.data);
        setCitiesLoading(false);
      } catch (error) {
      } finally {
        setCitiesLoading(false);
      }
    };
    getcities();
  }, [direction]);

  useEffect(() => {
    const getOperationStoreCategories = async () => {
      try {
        const { data } = await EndPoints.operationStoreCategories.getAllOperationStoreCategories({
          light: true,
          perPage: -1,
        });
        setOperationStoreCategories(data.data);
      } catch (error) {
      } finally {
        setOperationStoreCategoriesLoading(false);
      }
    };
    getOperationStoreCategories();
  }, []);

  const fetchStores = async (filter?: boolean | null, categoryId?: number | null) => {
    try {
      const filters = [
        {
          name: "status",
          value: 1,
          operation: "eq",
        }
      ];

      if (filter !== null) {
        filters.push({
          name: "pharmacyPromotion",
          value: filter ? 1 : 0,
          operation: "eq",
        });
      }

      if (categoryId !== null) {
        filters.push({
          name: "operationStoreCategoryId",
          value: categoryId,
          operation: "eq",
        });
      }

      const { data } = await EndPoints.store.getAllStores({
        light: true,
        perPage: -1,
        filters: filters
      });
      setStoresData(data.data);
      setStoresLoading(false);
    } catch (error) {
      console.error("Error fetching stores:", error);
    } finally {
      setStoresLoading(false);
    }
  };

  useEffect(() => {
    fetchStores(pharmacyPromotionFilter, operationStoreCategoryFilter);
  }, [pharmacyPromotionFilter, operationStoreCategoryFilter]);

  const methods = useForm<any>({
    defaultValues: {
      name: getTranslations(details?.translations?.name),
      note: getTranslations(details?.translations?.note) ?? null,
      cityId: details?.city?.id,
      status: details?.status,
    } as IUpdateArea,
  });

  const {
    getValues,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = methods;

  const pointMapChanges = useWatch({
    control,
    name: "points",
  });

  // Block submission until the drawn polygon passes the same rules the backend enforces.
  const polygonValidation = validatePolygonCoordinates(
    pointMapChanges?.coordinates,
  );

  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const handlePolygonChange = (polygonData: any) => {
    setValue("points", polygonData);

  };

  const onSubmit = async (data: any) => {
    const note = await getDumpTranslations(data?.note);
    await execute({
      callback: async () => {
        setLoadingSubmit(true);
        details
          ? await EndPoints.discountarea.updateArea(details?.id, {
            ...data,
            points: {
              type: "Polygon",
              coordinates: data?.points?.coordinates,
            },
            note: note,
          })
          : await EndPoints.discountarea.createArea({
            ...data,
            points: data?.points
              ? {
                type: "Polygon",
                coordinates: data?.points?.coordinates,
              }
              : undefined,

            note: note,
          });
        //Todo un commit
        // actions.getDetails(Number(id));
        navigate(-1);
      },
      fallback: (error) => { },
      finallyCallback: () => {
        setLoadingSubmit(false);
      },
      throwException: false,
    });
  };

  useEffect(() => {
    const prevPints = getValues("points");
    reset({
      name: getTranslations(details?.translations?.name),
      note: getTranslations(details?.translations?.note),
      cityId: details?.city?.id,
      status: details?.status,
      points: details?.points?.coordinates
        ? {
          type: "Polygon",
          coordinates: details?.points?.coordinates,
        }
        : {
          type: "Polygon",
          coordinates: prevPints?.coordinates,
        },
    } as IUpdateArea);
  }, [details]);


  useEffect(() => {
    fetchStores();
  }, [direction]);

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <span className={styles.title}>
            {details ? t("update_area") : t("add_area")}
          </span>
          <Divider className={styles.divider} />
          {loading.includes("details") || storesLoading ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <div className={styles.children}>
              <Row gutter={[16, 8]}>
                {languages?.map((lang, index) => {
                  return (
                    <Col span={12}>
                      <FieldBuilder
                        key={index}
                        name={`name[${lang?.code}]`}
                        label={`${t("name")} (${lang?.name})`}
                        input={{ type: "text" }}
                        rules={{ required: true }}
                      />
                    </Col>
                  );
                })}

                <Col span={12}>
                  <FieldBuilder
                    name="cityId"
                    label={`${t("city")}`}
                    width="large"
                    rules={{ required: true }}
                    input={{
                      type: "select",
                      loading: citiesLoading,
                      allowSearch: true,
                      options: cities?.map((role, index) => {
                        return {
                          key: index,
                          label: role.name,
                          value: role.id,
                        };
                      }),
                      onChange: (val) => {
                        getAreas(val);
                      },
                    }}
                  />
                </Col>
                <Col span={12}>
                  <FieldBuilder
                    name="status"
                    label={`${t("status")}`}
                    width="large"
                    rules={{ required: true }}
                    input={{
                      type: "select",
                      loading: false,
                      options: status?.map((state, index) => {
                        return {
                          key: index,
                          label: t(getAreaStatus(state)),
                          value: state,
                        };
                      }),
                    }}
                  />
                </Col>
                {languages?.map((lang, index) => {
                  return (
                    <Col span={12}>
                      <FieldBuilder
                        key={index}
                        name={`note[${lang?.code}]`}
                        label={`${t("note")} (${lang?.name})`}
                        input={{ type: "text-area" }}
                        rules={{ required: false }}
                      />
                    </Col>
                  );
                })}

                <Col span={12}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder={t("filter_by_promotion")}
                    onChange={(value) => setPharmacyPromotionFilter(value)}
                    options={[
                      {
                        value: null,
                        label: t("all_stores"),
                      },
                      {
                        value: true,
                        label: t("promoted_stores"),
                      },
                      {
                        value: false,
                        label: t("non_promoted_stores"),
                      },
                    ]}
                  />
                </Col>
                <Col span={12}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder={t("filter_by_category")}
                    onChange={(value) => setOperationStoreCategoryFilter(value)}
                    loading={operationStoreCategoriesLoading}
                    options={[
                      {
                        value: null,
                        label: t("all_categories"),
                      },
                      ...operationStoreCategories.map(category => ({
                        value: category.id,
                        label: category.name,
                      }))
                    ]}
                  />
                </Col>

                <Col span={24}>
                  <FormItem label={t("map")} required>
                    <Controller
                      name="points"
                      control={control}
                      rules={{ required: false }}
                      render={({ field: { ...field } }) => {
                        return (
                          <CombinedMap
                            editable
                            draggable
                            onChange={handlePolygonChange}
                            onGeometryIssuesChange={setMapGeometryIssues}
                            stores={storesData}
                            points={field.value}
                            allPoints={area}
                            serviceAreaPoints={serviceAreas}
                            currentAreaId={details?.id}
                            mapWidth="100%"
                            mapHeight="60vh"
                          />
                        );
                      }}
                    />
                  </FormItem>
                </Col>
              </Row>
            </div>
          )}
        </div>

        <div>
          <Divider className={styles.divider} />
          <div className={styles.formFooter}>
            <Button
              onClick={() => {
                navigate(-1);
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              loading={loadingSubmit}
              disabled={
                !polygonValidation.valid ||
                loadingArea ||
                mapGeometryIssues.outsideServiceArea > 0
              }
              htmlType="submit"
              type="primary"
            >
              {t("save")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const AreaForm = () => {
  return (
    <AreaContextProvider>
      <Form />
    </AreaContextProvider>
  );
};
export default AreaForm;
